import React, { useState, useEffect } from 'react'
import { Box, Typography, TextField, CircularProgress, FormHelperText, Chip } from '@mui/material'
import Modal from '../../../components/Modal'
import LanguageStrings from '../../../i18n/locales'
import { GenXDropdown, type Option } from '../../../components/GenXDropdown'
import interceptedAxios from '../../../utils/interceptedAxios'
import { fetchEmailGroups, createEmailAlertGroup, updateEmailAlertGroup } from '../redux/actionCreators'
import { type CreateEmailGroupRequest } from '../services/createEmailGroupService'
import { type UpdateEmailGroupRequest } from '../services/updateEmailGroupService'
import CustomToast from './ToastComponent'
import { connect, type ConnectedProps } from 'react-redux'

const emailNotificationStrings = LanguageStrings().ThermalIQStrings.EmailNotificationComponent

interface EmailAlertModalProps {
  open: boolean
  onClose: () => void
  groupId?: string // Optional for create, required for update
  mode: 'create' | 'update'
}

const EmailAlertModal = (props: PropsFromRedux & EmailAlertModalProps): JSX.Element => {
  const [groupName, setGroupName] = useState<string>('')
  const [tenantId, setTenantId] = useState<string>('')
  const [groupNameError, setGroupNameError] = useState<boolean>(false)
  const [groupNameErrorMessage, setGroupNameErrorMessage] = useState<string>('')
  const [selectedSites, setSelectedSites] = useState<Option[]>([])
  const [alarmSeverityList, setAlarmSeverityList] = useState<Option[]>([])
  const [siteOptions, setSiteOptions] = useState<Option[]>([])
  const [alarmSeverityOptions, setAlarmSeverityOptions] = useState<Option[]>([])
  const [emailInput, setEmailInput] = useState<string>('')
  const [emailList, setEmailList] = useState<string[]>([])
  const [emailError, setEmailError] = useState<boolean>(false)
  const [emailErrorMessage, setEmailErrorMessage] = useState<string>('')
  const [alarmSeverityError, setAlarmSeverityError] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isSiteLoading, setIsSiteLoading] = useState<boolean>(false)
  const [isAlarmSeverityLoading, setIsAlarmSeverityLoading] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(props.mode === 'create')

  const isUpdateMode = props.mode === 'update'
  const regexValidator = /^[a-zA-Z0-9]+(?:[ _-][a-zA-Z0-9]+)*$/

  useEffect(() => {
    if (props.open) {
      setIsLoading(true)

      const tasks = [fetchSites(), fetchAlarmSeverities()]

      // If it's update mode and we have groupId, load existing data
      if (isUpdateMode && props.groupId != null && props.groupId !== '') {
        tasks.push(loadEmailGroupDetails())
      }

      Promise.all(tasks).finally(() => {
        setIsLoading(false)
      })
    }
  }, [props.open, props.groupId, isUpdateMode])

  useEffect(() => {
    if (props.open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'auto'
    }
    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [props.open])

  const loadEmailGroupDetails = async (): Promise<void> => {
    if (props.groupId == null || props.groupId === '') return

    try {
      const response = await interceptedAxios.get(`/communications-service/api/v1/udp/tiq/services/email/view-group/${props.groupId}`)
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      if (response?.data?.data) {
        const details = response.data.data

        // Set the form fields with existing data
        setGroupName(details.groupName ?? '')

        // Extract tenant ID if available
        if (response.data.tenantId != null && response.data.tenantId !== '') {
          setTenantId(response.data.tenantId)
        }

        // Parse emails from comma-separated string to array
        if (details.distributionList != null && details.distributionList !== '') {
          const emails = Array.isArray(details.distributionList)
            ? details.distributionList
            : (typeof details.distributionList === 'string' ? details.distributionList.split(',') : [])
          setEmailList(emails)
        }

        // Set selected sites
        if (details.siteDetails != null && typeof details.siteDetails === 'object') {
          const sites = Object.entries(details.siteDetails).map(([id, name]) => ({
            value: id,
            label: String(name)
          }))
          setSelectedSites(sites)
        }

        // Set alarm severities
        if (details.severityDetails != null && typeof details.severityDetails === 'object') {
          const severities = Object.entries(details.severityDetails).map(([id, name]) => ({
            value: id,
            label: String(name)
          }))
          setAlarmSeverityList(severities)
        }

        setIsDataLoaded(true)
      }
    } catch (error: any) {
      CustomToast({
        type: 'error',
        heading: 'Error',
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        message: error?.message ? error.message : 'Failed to load email group details'
      })
    }
  }

  const fetchSites = async (): Promise<void> => {
    setIsSiteLoading(true)
    try {
      const response = await interceptedAxios.get('/assetrest/api/v2/udp/tiq/sites', {
        params: {
          page: 1,
          pageSize: 100
        }
      })

      if (
        response.data !== undefined &&
        response.data.error === false &&
        response.data.data !== undefined &&
        response.data.data.sites !== undefined &&
        Array.isArray(response.data.data.sites)
      ) {
        const sites = response.data.data.sites.map((site: any) => ({
          label: site.siteName,
          value: site.id
        }))
        setSiteOptions(sites)
      } else {
        CustomToast({
          type: 'error',
          heading: 'Error',
          message: 'Failed to load site data'
        })
      }
    } catch (error: any) {
      if (
        typeof error === 'object' &&
        error !== null &&
        typeof error.response === 'object' &&
        error.response !== null &&
        typeof error.response.data === 'object' &&
        error.response.data !== null &&
        typeof error.response.data.error_message === 'string'
      ) {
        CustomToast({
          type: 'error',
          heading: 'Error',
          message: error.response.data.error_message
        })
      } else {
        CustomToast({
          type: 'error',
          heading: 'Error',
          message: 'Failed to load site data'
        })
      }
    } finally {
      setIsSiteLoading(false)
    }
  }

  const fetchAlarmSeverities = async (): Promise<void> => {
    setIsAlarmSeverityLoading(true)
    try {
      const response = await interceptedAxios.get('/asset-config-service/api/v1/udp/tiq/commands/configuration/alarm/metadata')

      if (
        response.data !== undefined &&
        response.data.error === false &&
        response.data.data !== undefined &&
        response.data.data.alarmSeverities !== undefined &&
        Array.isArray(response.data.data.alarmSeverities)
      ) {
        const severities = response.data.data.alarmSeverities.map((severity: any) => ({
          label: severity.severityLevel,
          value: severity.alarmSeverityId
        }))
        setAlarmSeverityOptions(severities)
      } else {
        CustomToast({
          type: 'error',
          heading: 'Error',
          message: 'Failed to load alarm severity data'
        })
      }
    } catch (error: any) {
      if (typeof error?.response?.data?.error_message === 'string') {
        CustomToast({
          type: 'error',
          heading: 'Error',
          message: error.response.data.error_message
        })
      } else {
        CustomToast({
          type: 'error',
          heading: 'Error',
          message: 'Failed to load alarm severity data'
        })
      }
    } finally {
      setIsAlarmSeverityLoading(false)
    }
  }

  const handleGroupNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value
    setGroupName(value)
    if (value.trim() === '') {
      setGroupNameError(false)
      setGroupNameErrorMessage('')
    } else if (value.length > 30) {
      setGroupNameError(true)
      setGroupNameErrorMessage('Group name must be less than or equal to 30 characters')
    } else if (!regexValidator.test(value)) {
      setGroupNameError(true)
      setGroupNameErrorMessage('Please enter a valid group name. Only nonconsecutive space, _, - are allowed')
    } else {
      setGroupNameError(false)
      setGroupNameErrorMessage('')
    }
  }

  const handleSiteChange = (option: Option): void => {
    if (selectedSites.some(site => site.value === option.value)) return
    setSelectedSites([...selectedSites, option])
  }

  const removeSite = (siteToRemove: Option): void => {
    setSelectedSites(selectedSites.filter(site => site.value !== siteToRemove.value))
  }

  const handleAlarmSeverityChange = (option: Option): void => {
    if (alarmSeverityList.some(severity => severity.value === option.value)) return
    setAlarmSeverityList([...alarmSeverityList, option])
    setAlarmSeverityError(false)
  }

  const removeAlarmSeverity = (severityToRemove: Option): void => {
    setAlarmSeverityList(alarmSeverityList.filter(severity => severity.value !== severityToRemove.value))
  }

  const handleEmailInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setEmailInput(e.target.value)
    setEmailError(false)
    setEmailErrorMessage('')
  }

  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter' && emailInput.trim() !== '') {
      e.preventDefault()
      addEmail()
    }
  }

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  const addEmail = (): void => {
    const email = emailInput.trim()
    if (email === '') return

    if (emailList.length >= 15) {
      setEmailError(true)
      setEmailErrorMessage('You can add a maximum of 15 email addresses')
      return
    }

    if (!validateEmail(email)) {
      setEmailError(true)
      setEmailErrorMessage('Email ID is incorrect, please enter a valid email')
      return
    }

    if (emailList.includes(email)) {
      setEmailError(true)
      setEmailErrorMessage(`The email address '${email}' has been entered more than once`)
      return
    }

    setEmailList([...emailList, email])
    setEmailInput('')
    setEmailError(false)
    setEmailErrorMessage('')
  }

  const removeEmail = (emailToRemove: string): void => {
    setEmailList(emailList.filter(email => email !== emailToRemove))
  }

  const handleSubmit = (): void => {
    // Validate form
    if (groupName.trim() === '') {
      setGroupNameError(true)
      setGroupNameErrorMessage('Group name is required')
      return
    }
    if (!regexValidator.test(groupName.trim())) {
      setGroupNameError(true)
      setGroupNameErrorMessage('Please enter a valid group name. Only nonconsecutive space, _, - are allowed')
      return
    }
    if (selectedSites.length === 0) {
      return
    }
    if (alarmSeverityList.length === 0) {
      setAlarmSeverityError(true)
      return
    }
    if (emailList.length === 0) {
      setEmailError(true)
      setEmailErrorMessage('At least one email is required')
      return
    }

    const commonData = {
      groupName,
      siteIds: selectedSites.map(site => site.value),
      severityIds: alarmSeverityList.map(severity => severity.label).join(','),
      distributionList: emailList.join(','),
      enabled: true
    }

    setIsSubmitting(true)
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (isUpdateMode && props.groupId) {
      // Update mode
      const updateData: UpdateEmailGroupRequest = {
        ...commonData,
        tenantId
      }

      props.updateEmailAlertGroup(
        props.groupId,
        updateData,
        (response: any, httpStatus: number) => {
          CustomToast({
            type: 'success',
            heading: 'Success',
            message: 'Email alert group updated successfully'
          })
          props.fetchEmailGroups(0, 10, true)
          handleClose()
          setIsSubmitting(false)
        },
        (errorMessage: string, httpStatus: number) => {
          CustomToast({
            type: 'error',
            heading: 'Error',
            message: errorMessage
          })
          setIsSubmitting(false)
        }
      )
    } else {
      // Create mode
      const createData: CreateEmailGroupRequest = commonData as any

      props.createEmailAlertGroup(
        createData,
        (response: any, httpStatus: number) => {
          CustomToast({
            type: 'success',
            heading: 'Success',
            message: 'Email alert group created successfully, A test email has been dispatched to the designated user for the purpose of email alarm notification.'
          })
          props.fetchEmailGroups(0, 10, true)
          handleClose()
          setIsSubmitting(false)
        },
        (errorMessage: string, httpStatus: number) => {
          CustomToast({
            type: 'error',
            heading: 'Error',
            message: errorMessage
          })
          setIsSubmitting(false)
        }
      )
    }
  }

  const handleClose = (): void => {
    setGroupName('')
    setSelectedSites([])
    setEmailInput('')
    setEmailList([])
    setGroupNameError(false)
    setGroupNameErrorMessage('')
    setEmailError(false)
    setEmailErrorMessage('')
    setAlarmSeverityList([])
    setIsDataLoaded(props.mode === 'create')
    props.onClose()
  }

  const isFormValid = (): boolean => {
    return (
      groupName.trim() !== '' &&
      !groupNameError &&
      regexValidator.test(groupName.trim()) &&
      selectedSites.length > 0 &&
      alarmSeverityList.length > 0 &&
      emailList.length > 0 &&
      !emailError &&
      !alarmSeverityError
    )
  }

  const getModalTitle = (): string => {
    return isUpdateMode ? emailNotificationStrings.UpdateEmailAlarm : emailNotificationStrings.EmailAlarm
  }

  const getDescriptionText = (): string => {
    return isUpdateMode ? emailNotificationStrings.EditEmailGroupInformation : emailNotificationStrings.EmailGroupInformation
  }

  const getButtonLabel = (): string => {
    return isUpdateMode ? 'Update' : 'Create'
  }

  const getLoadingText = (): string => {
    return isUpdateMode ? emailNotificationStrings.UpdatingEmailAlertGroup : emailNotificationStrings.CreatingEmailAlertGroup
  }

  return (
    <Modal
      title={getModalTitle()}
      onClose={handleClose}
      height={'auto'}
      LabelNegative='Cancel'
      LabelPositive={getButtonLabel()}
      buttonTypePositive='filled'
      buttonTypeNegative='hollow'
      onClickNegative={handleClose}
      onClickPositive={handleSubmit}
      positiveDisabled={!isFormValid() || isSubmitting || isLoading || (isUpdateMode && !isDataLoaded)}
    >
      {/* Header and description remain outside the scrollable area */}
      <Typography sx={{
        marginBottom: 1,
        marginTop: 1,
        fontWeight: 500,
        fontFamily: 'Honeywell Sans Web',
        fontSize: '16px',
        lineHeight: '24px',
        verticalAlign: 'middle',
        textAlign: 'left',
        width: '100%',
        fontStyle: 'italic',
        color: '#B0B0B0',
        zIndex: 2
      }}>
        {getDescriptionText()}
      </Typography>
      <Box sx={{
        maxHeight: '60vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        width: '100%',
        position: 'relative',
        paddingRight: '10px'
      }}>
        {isLoading
          ? (
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '40px 0' }}>
                <CircularProgress size={40} />
              </div>
            )
          : (
          <>
            <Box sx={{
              paddingTop: '5px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'center',
              width: '100%'
            }}>
              <Box sx={{ marginBottom: 2, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography sx={{
                  marginBottom: 1,
                  fontWeight: 'normal',
                  fontFamily: 'Honeywell Sans Web',
                  fontSize: '16px',
                  lineHeight: '24px',
                  verticalAlign: 'middle',
                  textAlign: 'left',
                  width: '100%',
                  color: '#F0F0F0'
                }}>
                  {emailNotificationStrings.GroupName}*
                </Typography>
                <TextField
                  fullWidth
                  value={groupName}
                  onChange={handleGroupNameChange}
                  placeholder="Enter group name"
                  error={groupNameError}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#313030',
                      '& fieldset': {
                        borderColor: groupNameError ? '#f44336' : '#444'
                      },
                      '&:hover fieldset': {
                        borderColor: groupNameError ? '#f44336' : '#444'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: groupNameError ? '#f44336' : '#444'
                      }
                    },
                    '& .MuiInputBase-input': {
                      color: '#F0F0F0',
                      fontFamily: 'Honeywell Sans Web',
                      fontSize: '14px',
                      fontWeight: 'normal',
                      fontStyle: 'normal !important',
                      textDecoration: 'none',
                      '&::placeholder': {
                        color: '#A0A0A0',
                        opacity: 1,
                        fontFamily: 'Honeywell Sans Web',
                        fontSize: '14px',
                        fontWeight: 'normal',
                        fontStyle: 'normal !important',
                        textDecoration: 'none'
                      }
                    }
                  }}
                  inputProps={{ 'data-testid': 'group-name-input' }}
                  disabled={isSubmitting}
                />
                {groupNameError && (
                  <FormHelperText sx={{ color: '#f44336', marginTop: 1, marginLeft: 1 }}>
                    {groupNameErrorMessage !== '' ? groupNameErrorMessage : 'Group name is required'}
                  </FormHelperText>
                )}
              </Box>
              <Box sx={{ marginBottom: 2, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography sx={{
                  marginBottom: 1,
                  fontWeight: 'normal',
                  fontFamily: 'Honeywell Sans Web',
                  fontSize: '16px',
                  lineHeight: '24px',
                  verticalAlign: 'middle',
                  textAlign: 'left',
                  width: '100%',
                  color: '#F0F0F0'
                }}>
                  {emailNotificationStrings.ChooseSite}*
                </Typography>
                {isSiteLoading
                  ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center' }}>
                        <CircularProgress size={24} sx={{ margin: '10px 0' }} />
                      </div>
                    )
                  : (
                      <GenXDropdown
                        options={siteOptions.filter(opt => !selectedSites.some(site => site.value === opt.value))}
                        value={null}
                        onSelect={handleSiteChange}
                        placeholder="Select a site"
                        label=""
                        inputId="chooseSites"
                        disabled={isSubmitting}
                        width={'100%'}
                      />
                    )
                }
                {selectedSites.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, marginTop: 2, justifyContent: 'center' }}>
                    {selectedSites.map((site, index) => (
                      <Chip
                        key={index}
                        label={site.label}
                        onDelete={isSubmitting ? undefined : () => { removeSite(site) }}
                        sx={{
                          backgroundColor: 'transparent',
                          color: '#F0F0F0',
                          borderRadius: '4px',
                          border: '1px solid #444',
                          height: '32px',
                          fontFamily: 'Honeywell Sans Web',
                          fontWeight: 500,
                          fontSize: '14px',
                          '.MuiChip-deleteIcon': {
                            color: '#F0F0F0',
                            background: 'none',
                            borderRadius: 0,
                            '&:hover': {
                              color: '#FF5252',
                              background: 'none'
                            }
                          },
                          opacity: isSubmitting ? 0.7 : 1
                        }}
                        data-testid={`site-chip-${index}`}
                      />
                    ))}
                  </Box>
                )}
              </Box>

              <Box sx={{ marginBottom: 2, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography sx={{
                  marginBottom: 1,
                  fontWeight: 'normal',
                  fontFamily: 'Honeywell Sans Web',
                  fontSize: '16px',
                  lineHeight: '24px',
                  verticalAlign: 'middle',
                  textAlign: 'left',
                  width: '100%',
                  color: '#F0F0F0'
                }}>
                  {emailNotificationStrings.ChooseAlarmSeverity}*
                </Typography>
                {isAlarmSeverityLoading
                  ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center' }}>
                        <CircularProgress size={24} sx={{ margin: '10px 0' }} />
                        <Typography variant="body2" color="#A0A0A0">{emailNotificationStrings.LoadingAlarmSeverities}</Typography>
                      </div>
                    )
                  : (
                      <GenXDropdown
                        options={alarmSeverityOptions.filter(opt => !alarmSeverityList.some(sev => sev.value === opt.value))}
                        value={null}
                        onSelect={handleAlarmSeverityChange}
                        placeholder="Select alarm severity"
                        label=""
                        inputId="chooseSeverity"
                        disabled={isSubmitting}
                        width={'100%'}
                      />
                    )
                }
                {alarmSeverityList.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, marginTop: 2, justifyContent: 'center' }}>
                    {alarmSeverityList.map((severity, index) => (
                      <Chip
                        key={index}
                        label={severity.label}
                        onDelete={isSubmitting ? undefined : () => { removeAlarmSeverity(severity) }}
                        sx={{
                          backgroundColor: 'transparent',
                          color: '#F0F0F0',
                          borderRadius: '4px',
                          border: '1px solid #444',
                          height: '32px',
                          fontFamily: 'Honeywell Sans Web',
                          fontWeight: 500,
                          fontSize: '14px',
                          '.MuiChip-deleteIcon': {
                            color: '#F0F0F0',
                            background: 'none',
                            borderRadius: 0,
                            '&:hover': {
                              color: '#FF5252',
                              background: 'none'
                            }
                          },
                          opacity: isSubmitting ? 0.7 : 1
                        }}
                        data-testid={`severity-chip-${index}`}
                      />
                    ))}
                  </Box>
                )}
                {alarmSeverityList.length === 0 && alarmSeverityError && (
                  <FormHelperText sx={{ color: '#f44336', marginTop: 1, marginLeft: 1 }}>
                    {emailNotificationStrings.AddAtLeastOneAlarmSeverity}
                  </FormHelperText>
                )}
              </Box>

              <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography sx={{
                  marginBottom: 1,
                  fontWeight: 'normal',
                  fontFamily: 'Honeywell Sans Web',
                  fontSize: '16px',
                  lineHeight: '24px',
                  verticalAlign: 'middle',
                  textAlign: 'left',
                  width: '100%',
                  color: '#F0F0F0'
                }}>
                  {emailNotificationStrings.DistributionList}*
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={4}
                  value={emailInput}
                  onChange={handleEmailInputChange}
                  onKeyDown={handleEmailKeyDown}
                  placeholder="Enter email and press enter"
                  error={emailError}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#313030',
                      '& fieldset': {
                        borderColor: emailError ? '#f44336' : '#444'
                      },
                      '&:hover fieldset': {
                        borderColor: emailError ? '#f44336' : '#444'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: emailError ? '#f44336' : '#444'
                      }
                    },
                    '& .MuiInputBase-input': {
                      color: '#F0F0F0',
                      fontFamily: 'Honeywell Sans Web',
                      fontSize: '14px',
                      fontWeight: 'normal',
                      fontStyle: 'normal !important',
                      textDecoration: 'none',
                      '&::placeholder': {
                        color: '#A0A0A0',
                        opacity: 1,
                        fontFamily: 'Honeywell Sans Web',
                        fontSize: '14px',
                        fontWeight: 'normal',
                        fontStyle: 'normal !important',
                        textDecoration: 'none'
                      }
                    }
                  }}
                  inputProps={{ 'data-testid': 'email-input' }}
                  disabled={isSubmitting}
                />
                {emailError && (
                  <FormHelperText sx={{ color: '#f44336', marginTop: 1, marginLeft: 1 }}>
                    {emailErrorMessage}
                  </FormHelperText>
                )}
                {/* Show chips for each email in the list */}
                {emailList.length > 0 && (
                  <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    marginTop: 1,
                    marginBottom: 1,
                    justifyContent: 'flex-start',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    borderRadius: '8px',
                    padding: '8px',
                    backgroundColor: 'transparent',
                    '&::-webkit-scrollbar': {
                      width: '8px'
                    },
                    '&::-webkit-scrollbar-track': {
                      background: 'rgba(255, 255, 255, 0.00)'
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: 'rgba(255, 255, 255, 0.50)',
                      borderRadius: '4px'
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      background: 'rgba(255, 255, 255, 0.70)'
                    }
                  }}>
                    {emailList.map((email, index) => (
                      <Chip
                        key={index}
                        label={email}
                        onDelete={isSubmitting ? undefined : () => { removeEmail(email) }}
                        sx={{
                          backgroundColor: 'transparent',
                          color: '#F0F0F0',
                          borderRadius: '6px',
                          border: '1px solid #444',
                          height: '36px',
                          fontFamily: 'Honeywell Sans Web',
                          fontWeight: 400,
                          fontSize: '14px',
                          maxWidth: '350px',
                          '& .MuiChip-label': {
                            paddingLeft: '16px',
                            paddingRight: '8px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          },
                          '& .MuiChip-deleteIcon': {
                            color: '#FFFFFF',
                            backgroundColor: 'transparent',
                            borderRadius: '50%',
                            marginRight: '6px',
                            fontSize: '16px',
                            width: '20px',
                            height: '20px',
                            border: 'none',
                            '&:hover': {
                              color: '#FF5252',
                              backgroundColor: 'transparent'
                            }
                          },
                          opacity: isSubmitting ? 0.7 : 1
                        }}
                        data-testid={`email-chip-${index}`}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
            {/* Removed validation messages block after all fields (previously commented out) */}
          </>
            )}
      </Box>
      {/* Fullscreen overlay for submitting state - render at the end of Modal content so it covers everything */}
      {isSubmitting && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 13000, // higher than modal
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CircularProgress size={48} thickness={4} />
            <Typography sx={{ color: '#F0F0F0', mt: 2, fontFamily: 'Honeywell Sans Web', fontWeight: 500 }}>
              {getLoadingText()}
            </Typography>
          </Box>
        </Box>
      )}
    </Modal>
  )
}

interface DispatchToProps {
  fetchEmailGroups: (
    page: number,
    pageSize: number,
    sortEnabled: boolean
  ) => void
  createEmailAlertGroup: (
    data: CreateEmailGroupRequest,
    onSuccess: (response: any, httpStatus: number) => void,
    onFailure: (errorMessage: string, httpStatus: number) => void
  ) => void
  updateEmailAlertGroup: (
    groupId: string,
    data: UpdateEmailGroupRequest,
    onSuccess: (response: any, httpStatus: number) => void,
    onFailure: (errorMessage: string, httpStatus: number) => void
  ) => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchEmailGroups: (page: number, pageSize: number, sortEnabled: boolean) =>
    dispatch(fetchEmailGroups(page, pageSize, sortEnabled)),
  createEmailAlertGroup: (data, onSuccess, onFailure) =>
    dispatch(createEmailAlertGroup(data, onSuccess, onFailure)),
  updateEmailAlertGroup: (groupId, data, onSuccess, onFailure) =>
    dispatch(updateEmailAlertGroup(groupId, data, onSuccess, onFailure))
})

const connector = connect(null, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(EmailAlertModal)
